import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, Users, GraduationCap, BarChart3, UserPlus, Network, Check, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const IMG = "https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const SOLUTIONS = [
  { icon: UserPlus, t: "Recruiting Technology", d: "Attract and screen caregiving talent faster." },
  { icon: Users, t: "Onboarding", d: "Streamlined new-hire onboarding and documentation." },
  { icon: GraduationCap, t: "Training Management", d: "Assign and track T5 Academy courses across your team." },
  { icon: BarChart3, t: "Workforce Analytics", d: "Retention insights, capacity and scheduling intelligence." },
  { icon: Network, t: "Marketplace Access", d: "Tap the T5 Connect caregiver network." },
  { icon: Sparkles, t: "AI Operational Tools", d: "AI-assisted matching and staffing recommendations." },
];
const TIERS = [
  { name: "T5 Workforce Pro", price: "from $10,000/yr", tag: "Growing agencies & multi-location care", points: ["Platform access", "Recruiting & onboarding", "Training management", "Workforce analytics", "Standard support"] },
  { name: "T5 Enterprise", price: "Custom annual contract", tag: "Health systems & large organizations", points: ["Everything in Pro", "Implementation & custom onboarding", "Multiple locations & admin dashboards", "Custom integrations (API/CRM)", "Dedicated support"], featured: true },
];
const METRICS = [["Workforce Size", "312"], ["Available Staff", "74"], ["Open Positions", "9"], ["Open Shifts", "21"], ["Training Completion", "88%"], ["New Applicants", "17"]];

export default function Businesses() {
  const navigate = useNavigate();
  return (
    <div>
      <section className="bg-blue-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.16em] text-teal-400 font-semibold mb-3">T5 Business Solutions</p>
            <h1 className="font-display text-4xl lg:text-5xl font-bold leading-tight">Build a stronger care workforce.</h1>
            <p className="mt-4 text-blue-100/80 text-lg">Recruiting, training, scheduling and analytics for home-care agencies, senior-care organizations, healthcare companies and employers.</p>
            <div className="mt-7 flex gap-3"><Button onClick={() => navigate("/request-demo")} data-testid="biz-demo" className="h-12 px-7 bg-teal-500 hover:bg-teal-600 text-white">Request a Demo</Button></div>
          </div>
          <img src={IMG} alt="Care organization team" className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/2.8]" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-display text-3xl font-bold text-blue-950 text-center mb-10">Solutions built for care organizations</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SOLUTIONS.map((s, i) => (
            <motion.div key={s.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.06 }}
              className="bg-white rounded-2xl p-6 border border-blue-900/5 shadow-sm">
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-teal-50 text-teal-700 mb-4"><s.icon className="w-6 h-6" /></span>
              <h3 className="font-display font-semibold text-blue-950">{s.t}</h3>
              <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Dashboard concept */}
      <section className="bg-white border-y border-blue-900/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.16em] text-teal-600 font-semibold mb-3">T5 Workforce Dashboard</p>
            <h2 className="font-display text-3xl font-bold text-blue-950">See your whole workforce at a glance.</h2>
            <p className="mt-3 text-slate-600 leading-relaxed">Track staffing, applicants, training and shifts in real time — with AI insights that flag risks before they happen.</p>
            <div className="mt-4 rounded-xl bg-blue-950 text-white p-4 flex items-start gap-2.5"><Sparkles className="w-4 h-4 text-teal-400 mt-0.5 shrink-0" /><p className="text-sm">AI Insight: "Tuesday has the highest projected staffing shortage."</p></div>
          </div>
          <div className="bg-[#FAF9F6] rounded-2xl p-5 border border-blue-900/5 shadow-lg">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {METRICS.map(([l, v]) => <div key={l} className="rounded-xl bg-white border border-slate-100 p-3"><p className="font-display text-xl font-bold text-blue-950">{v}</p><p className="text-[11px] text-slate-500 leading-tight">{l}</p></div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-display text-3xl font-bold text-blue-950 text-center mb-10">Enterprise solutions</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {TIERS.map((t) => (
            <div key={t.name} data-testid={`tier-${t.name.replace(/\s+/g, "-").toLowerCase()}`}
              className={`rounded-2xl p-8 border shadow-sm flex flex-col ${t.featured ? "bg-blue-950 text-white border-blue-950" : "bg-white border-blue-900/5"}`}>
              <h3 className="font-display text-2xl font-bold">{t.name}</h3>
              <p className={`text-sm mt-1 ${t.featured ? "text-teal-300" : "text-teal-700"}`}>{t.tag}</p>
              <p className="font-display text-3xl font-bold mt-4">{t.price}</p>
              <ul className="mt-5 space-y-2 flex-1">{t.points.map((p) => <li key={p} className={`flex items-center gap-2 text-sm ${t.featured ? "text-blue-100" : "text-slate-600"}`}><Check className={`w-4 h-4 shrink-0 ${t.featured ? "text-teal-400" : "text-teal-500"}`} /> {p}</li>)}</ul>
              <Button onClick={() => navigate("/request-demo")} className={`mt-6 h-12 ${t.featured ? "bg-teal-500 hover:bg-teal-600 text-white" : "bg-blue-950 hover:bg-blue-900 text-white"}`}>Request a Demo</Button>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-slate-400 mt-6">Pricing reflects scope and is confirmed during your consultation. We don't promise specific outcomes or ROI.</p>
      </section>
    </div>
  );
}
